import { LinkedinSettings } from '../linkedinSettings';

export type CronoGatewayLinkedinAction =
  | CronoGatewayLinkedinVerifyLoginAction
  | CronoGatewayLinkedinGetStatusAction
  | CronoGatewayLinkedinSendInvitationAction
  | CronoGatewayLinkedinSendMessageAction
  | CronoGatewayLinkedinGetConversationAction
  | CronoGatewayLinkedinGetActivitiesAction
  | CronoGatewayLinkedinReactLinkedinActivityAction
  | CronoGatewayLinkedinGetContactSocialInteractionsAction
  | CronoGatewayLinkedinGetCompanyActivitiesAction
  | CronoGatewayLinkedinGetContactCommentsAction
  | CronoGatewayLinkedinGetContactEventsAction
  | CronoGatewayLinkedinGetContactExperiencesAction
  | CronoGatewayLinkedinGetCompanyJobsAction
  | CronoGatewayLinkedinSearchLeadsAction
  | CronoGatewayLinkedinGetProfileViewAction
  | CronoGatewayLinkedinGetCompanyExtendedInfoAction
  | CronoGatewayLinkedinSearchCompaniesAction
  | CronoGatewayLinkedinSearchSalesNavLeadsAction
  | CronoGatewayLinkedinSearchSalesNavCompaniesAction
  | CronoGatewayLinkedinGetCompanyInfoAction
  | CronoGatewayLinkedinGetAllConversationsAction
  | CronoGatewayLinkedinGetSuggestedCompaniesAction
  | CronoGatewayLinkedinGetProfileInfoAction
  | CronoGatewayLinkedinGetInvitationsAction
  | CronoGatewayLinkedinGetProfileIdFromLeadIdAction
  | CronoGatewayLinkedinGetCompanyInfoByNumericIdAction
  | CronoGatewayLinkedinGetCompanyNumericIdAction
  | CronoGatewayLinkedinGetUserExperienceAction
  | CronoGatewayLinkedinGetThreadReactionsAction
  | CronoGatewayLinkedinHasSalesNavAction
  | CronoGatewayLinkedinHasSalesNav2Action
  | CronoGatewayLinkedinGetInvitationsLimitAction
  | CronoGatewayLinkedinGetJobChangesAction
  | CronoGatewayLinkedinSendMessageWithAttachmentAction
  | CronoGatewayLinkedinSendVoiceMessageAction
  | CronoGatewayLinkedinSendInMailMessageAction
  | CronoGatewayLinkedinGetInMailCreditsAction
  | CronoGatewayLinkedinGetSalesNavConversationAction
  | CronoGatewayLinkedinGetSalesNavConversationIdAction
  | CronoGatewayLinkedinGetRecentConnections
  | CronoGatewayLinkedinDownloadFileAction
  | CronoGatewayLinkedinGetSalesNavAllConversationsAction
  | CronoGatewayLinkedinGetFilterValuesAction
  | CronoGatewayLinkedinRefreshCurrentSalesNavCookie
  | CronoGatewayLinkedinGetConversationIdAction
  | CronoGatewayLinkedinGetProfileUrnAction
  | CronoGatewayLinkedinGetLeadIdAction
  | CronoGatewaySalesNavGetSavedSearchesLeads
  | CronoGatewaySalesNavGetSavedSearchesCompanies
  | CronoGatewaySalesNavExecuteSavedSearchLeads
  | CronoGatewaySalesNavExecuteSavedSearchCompanies
  | CronoGatewayLinkedinGetUserProfileInfoAction
  | CronoGatewayLinkedinVisitProfileAction
  | CronoGatewayLinkedinFollowProfileAction
  | CronoGatewayLinkedinMarkConversationAsReadAction
  | CronoGatewayLinkedinGetProfileDescriptionAction;

export type ProspectIdentifiers = {
  publicId?: string | null;
  profileId?: string | null;
  url?: string | null;
  leadId?: string | null;
};

export type ProspectIdentifiersOptions = keyof ProspectIdentifiers;

export const ProspectIdentifiersList: ProspectIdentifiersOptions[] = [
  'publicId',
  'profileId',
  'leadId',
  'url',
];

export type AccountIdentifiers = {
  numericId?: string | null;
  publicId?: string | null;
  url?: string | null;
};

export type AccountIdentifiersOptions = keyof AccountIdentifiers;

export const AccountIdentifiersList: AccountIdentifiersOptions[] = [
  'publicId',
  'numericId',
  'url',
];

// Extract method names
type MethodNames = CronoGatewayLinkedinAction['methodName'];

// Utility type to get the action type by method name
type ActionByMethodName<M extends MethodNames> = Extract<
  CronoGatewayLinkedinAction,
  { methodName: M }
>;

// Helper type to map method names to parameter keys
type ExtractKeys<T extends CronoGatewayLinkedinAction> =
  readonly (keyof T['params'])[];
type RequiredParamsByMethodNameMapType = {
  [K in MethodNames]: ExtractKeys<ActionByMethodName<K>>;
};
/*
  This map contains the parameters that are actually required for each linkedin method
  They are used since in some cases we may have one identifier in the frontend (e.g
  leadId for the prospect) but the publicId is needed. The LinkedinDispatcher will
  take care of the translation from the ones provided by the FE and the ones needed
  indicated here
 */
export const RequiredParamsMap: RequiredParamsByMethodNameMapType = {
  getStatus: ['publicId'],
  getUserProfileInfo: [],
  downloadFile: [],
  getInMailCredits: [],
  getInvitationsLimit: [],
  getActivities: ['publicId'],
  getContactExperiences: ['publicId'],
  getContactComments: ['publicId'],
  getContactEvents: ['publicId'],
  getContactSocialInteractions: ['publicId'],
  getCompanyActivities: ['numericId'],
  getCompanyJobs: ['numericId'],
  searchLeads: [],
  getProfileView: ['publicId'],
  getCompanyExtendedInfo: ['publicId', 'numericId'],
  searchCompanies: [],
  searchSalesNavLeads: [],
  searchSalesNavCompanies: [],
  getCompanyInfo: ['publicId'],
  getAllConversations: [],
  getSuggestedCompanies: ['publicId'],
  getProfileInfo: ['publicId'],
  getAllInvitations: [],
  getCompanyInfoByNumericId: ['numericId'],
  getCompanyNumericId: ['publicId'],
  getUserExperience: ['publicId'],
  getThreadReactions: [],
  hasSalesNav: [],
  hasSalesNav2: [],
  sendMessageWithAttachment: ['publicId'],
  sendVoiceMessage: ['publicId'],
  sendInMailMessage: ['publicId'],
  getSalesNavConversation: ['profileId', 'leadId'],
  getSalesNavConversationId: ['profileId'],
  getRecentConnections: [],
  getConversation: ['profileId'],
  getConversationId: ['profileId'],
  getJobChanges: [],
  getSalesNavAllConversations: [],
  verifyLogin: [],
  getLinkedinFilterValues: [],
  getPublicIdFromLeadId: ['leadId'],
  sendInvitation: ['publicId'],
  sendMessage: ['publicId'],
  getProfileUrn: ['publicId'],
  getLeadId: ['publicId'],
  refreshCurrentSalesNavCookie: [],
  getSalesNavSavedSearchesLeads: [],
  getSalesNavSavedSearchesCompanies: [],
  executeSalesNavSavedSearchesLeads: [],
  executeSalesNavSavedSearchesCompanies: [],
  reactToLinkedinActivity: [],
  visitProfile: ['publicId'],
  followProfile: ['publicId'],
  markLinkedinConversationAsRead: ['profileId'],
  getProfileDescription: ['profileId'],
} as const;

export type CronoGatewayLinkedinVerifyLoginAction = {
  identifiers: null;
  target: 'linkedin';
  methodName: 'verifyLogin';
  params: void;
  result: {
    hasLinkedinCookie: boolean;
    hasSalesNavCookie: boolean;
    hasExpiredSalesNavCookie: boolean;
  };
};

export type CronoGatewayLinkedinGetUserProfileInfoAction = {
  identifiers: null;
  target: 'linkedin';
  methodName: 'getUserProfileInfo';
  params: void;
  result: {
    firstName?: string | null;
    lastName?: string | null;
    imageUrl?: string | null;
    publicId?: string | null;
    leadId?: string | null;
  } | null;
};

export type CronoGatewayLinkedinVisitProfileAction = {
  identifiers: 'prospect';
  target: 'linkedin';
  methodName: 'visitProfile';
  params: {
    publicId: string;
  };
  result: void;
};

export type CronoGatewayLinkedinFollowProfileAction = {
  identifiers: 'prospect';
  target: 'linkedin';
  methodName: 'followProfile';
  params: {
    publicId: string;
  };
  result: void;
};

export type CronoGatewayLinkedinGetStatusAction = {
  identifiers: 'prospect';
  target: 'linkedin';
  methodName: 'getStatus';
  params: {
    user?: string;
    publicId?: string;
    company?: string | null;
    //Passed to patch the prospect if wrong id found in the url
    prospectId?: string;
  };
  result: {
    status: LinkedinConnectionStatus;
    statusUnknownReason?: string;
    publicId?: string;
    profileId?: string;
    firstName?: string;
    lastName?: string;
    pictureUrl?: string | null;
    company?: LinkedinCompany | null;
  } & Partial<LinkedinProfile>;
};

export type CronoGatewayLinkedinSendInvitationAction = {
  identifiers: 'prospect';
  target: 'linkedin';
  methodName: 'sendInvitation';
  params: {
    user: string;
    publicId?: string;
    message: string;
    company: string | null;
  };
  result: {
    invitationSent: boolean;
    connectionStatus: LinkedinConnectionStatus;
    invitationsLimitReached: boolean | null;
    messageTooLong: boolean | null;
    connectionRecentlyWithdrawn: boolean | null;
    errorBody?: string | null;
  };
};

export type CronoGatewayLinkedinSendMessageAction = {
  identifiers: 'prospect';
  target: 'linkedin';
  methodName: 'sendMessage';
  params: {
    user: string;
    publicId?: string;
    message: string;
    company: string | null;
  };
  result: {
    messageSent: boolean;
    connectionStatus: LinkedinConnectionStatus;
  };
};

export type CronoGatewayLinkedinMarkConversationAsReadAction = {
  identifiers: 'prospect';
  target: 'linkedin';
  methodName: 'markLinkedinConversationAsRead';
  params: {
    profileId?: string;
  };
  result: {
    success: boolean;
  };
};

export type CronoGatewayLinkedinGetConversationAction = {
  identifiers: 'prospect';
  target: 'linkedin';
  methodName: 'getConversation';
  params: { profileId?: string };
  result: {
    conversationPresent: boolean;
    conversation?: LinkedinMessage[];
  };
};

export type CronoGatewayLinkedinGetConversationIdAction = {
  identifiers: 'prospect';
  target: 'linkedin';
  methodName: 'getConversationId';
  params: { profileId?: string };
  result: {
    conversationPresent: boolean;
    conversationId: string | null;
  };
};

export type CronoGatewayLinkedinGetProfileUrnAction = {
  identifiers: 'prospect';
  target: 'linkedin';
  methodName: 'getProfileUrn';
  params: { publicId: string };
  result: {
    urn: string;
  };
};

export type CronoGatewayLinkedinGetLeadIdAction = {
  identifiers: 'prospect';
  target: 'linkedin';
  methodName: 'getLeadId';
  params: { publicId: string };
  result: {
    leadId: string | null;
  };
};

export type CronoGatewayLinkedinGetActivitiesAction = {
  identifiers: 'prospect';
  target: 'linkedin';
  methodName: 'getActivities';
  params: { publicId?: string };
  result: {
    activities: LinkedinActivity[];
    allHashtags: {
      [key: string]: number;
    };
  };
};

export type CronoGatewayLinkedinReactLinkedinActivityAction = {
  identifiers: null;
  target: 'linkedin';
  methodName: 'reactToLinkedinActivity';
  params: { activityUrn: string; reaction: LinkedinReaction };
  result: {
    success: boolean;
  };
};

export type CronoGatewayLinkedinGetContactExperiencesAction = {
  identifiers: 'prospect';
  target: 'linkedin';
  methodName: 'getContactExperiences';
  params: { publicId?: string };
  result: {
    experiences: LinkedinWorkExperience[];
  };
};

export type CronoGatewayLinkedinGetContactCommentsAction = {
  identifiers: 'prospect';
  target: 'linkedin';
  methodName: 'getContactComments';
  params: { publicId?: string };
  result: {
    activities: LinkedinActivity[];
    allHashtags: {
      [key: string]: number;
    };
  };
};

export type CronoGatewayLinkedinGetContactEventsAction = {
  identifiers: 'prospect';
  target: 'linkedin';
  methodName: 'getContactEvents';
  params: { publicId?: string };
  result: {
    activities: LinkedinActivity[];
    allHashtags: {
      [key: string]: number;
    };
  };
};

export type ISocialPresence = {
  activities: LinkedinActivity[];
  allHashtags: {
    [key: string]: number;
  };
};

export type CronoGatewayLinkedinGetContactSocialInteractionsAction = {
  identifiers: 'prospect';
  target: 'linkedin';
  methodName: 'getContactSocialInteractions';
  params: {
    publicId?: string;
    activities: boolean;
    comments: boolean;
    events: boolean;
  };
  result: ISocialPresence;
};

export type CronoGatewayLinkedinGetCompanyActivitiesAction = {
  identifiers: 'account';
  target: 'linkedin';
  methodName: 'getCompanyActivities';
  params: { numericId?: string };
  result: {
    activities: LinkedinActivity[];
    allHashtags: {
      [key: string]: number;
    };
  };
};

export type CronoGatewayLinkedinGetCompanyJobsAction = {
  identifiers: 'account';
  target: 'linkedin';
  methodName: 'getCompanyJobs';
  params: { numericId?: string };
  result: {
    jobs: LinkedinJobPost[];
    total: number;
  };
};

export type CronoGatewayLinkedinSearchLeadsAction = {
  identifiers: null;
  target: 'linkedin';
  methodName: 'searchLeads';
  params: {
    keywords?: string | null;
    industry?: string | string[] | null;
    currentCompanies?:
      | {
          name: string;
          numericId: string;
        }[]
      | null;
    companyPublicId?: string | null;
    companyName?: string | null;
    userFirstName?: string | null;
    userLastName?: string | null;
    title?: string | string[] | null;
    geoUrn?: string | string[] | null;
    connectionLevel?: string | string[] | null;
    profileLanguage?: string | string[] | null;
    page?: number;
  };
  result: {
    profiles?: LinkedinProfile[];
    paging: LinkedinPaging;
  };
};

export type CronoGatewayLinkedinGetProfileViewAction = {
  identifiers: 'prospect';
  target: 'linkedin';
  methodName: 'getProfileView';
  params: {
    publicId?: string;
    publicIdOrProfileIdOrLeadId?: string;
  };
  result: {
    profile: LinkedinProfileExtendedInfo;
  };
};

export type CronoGatewayLinkedinGetProfileViewGraphQlAction = {
  identifiers: 'prospect';
  target: 'linkedin';
  methodName: '_getProfileViewGraphQl';
  params: {
    publicId?: string;
    publicIdOrProfileIdOrLeadId?: string;
  };
  result: {
    profile: LinkedinProfileExtendedInfo;
  };
};

export type CronoGatewayLinkedinGetProfileDescriptionAction = {
  identifiers: 'prospect';
  target: 'linkedin';
  methodName: 'getProfileDescription';
  params: {
    profileId?: string;
  };
  result: string;
};

export type CronoGatewayLinkedinGetCompanyExtendedInfoAction = {
  identifiers: 'account';
  target: 'linkedin';
  methodName: 'getCompanyExtendedInfo';
  params: { numericId?: string; publicId?: string };
  result: {
    company: LinkedinCompanyExtendedInfo | null;
  };
};

export type ICompanyExtendedInfo =
  CronoGatewayLinkedinGetCompanyExtendedInfoAction['result'];

export type ICompanyJobs = CronoGatewayLinkedinGetCompanyJobsAction['result'];

export type SalesNavSearchLeadsFilter = {
  currentCompanies?:
    | ({
        name: string;
        numericId: string | null;
        publicId?: string | null;
      } & LinkedinExcludeFilter)[]
    | null;
  currentCompanyHeadcount?: LinkedinFilterValue[] | null;
  companyHeadquarters?: LinkedinFilterValue[] | null;
  function?: (LinkedinFilterValue & LinkedinExcludeFilter)[] | null;
  title?: string | string[] | null;
  jobTitle?: (LinkedinFilterValueFreeSolo & LinkedinExcludeFilter)[] | null;
  pastJobTitle?: (LinkedinFilterValueFreeSolo & LinkedinExcludeFilter)[] | null;
  seniority?: (LinkedinFilterValue & LinkedinExcludeFilter)[] | null;
  yearsInCurrentPosition?: LinkedinFilterValue[] | null;
  yearsOfExperience?: LinkedinFilterValue[] | null;
  connectionLevel?: LinkedinFilterValue[] | null;
  industry?: (LinkedinFilterValue & LinkedinExcludeFilter)[] | null;
  geography?: (LinkedinFilterValue & LinkedinExcludeFilter)[] | null;
  accountHasBuyerIntent?: boolean | null;
  geographyPostalCode?: {
    values: (LinkedinFilterValue & LinkedinExcludeFilter)[] | null;
    selectedSubFilter: string;
  };
  persona?: LinkedinFilterValue[] | null;
  userFirstName?: string | string[] | null;
  userLastName?: string | string[] | null;
  followsYourCompany?: boolean | null;
  viewedYourProfile?: boolean | null;
  postedOnLinkedin?: boolean | null;
  profileLanguage?: LinkedinFilterValue[] | null;
};

export type CronoGatewayLinkedinSearchSalesNavLeadsAction = {
  identifiers: null;
  target: 'linkedin';
  methodName: 'searchSalesNavLeads';
  params: SalesNavSearchLeadsFilter & {
    keywords?: string | null;
    // pagination
    start?: number | null;
    count?: number | null;
  };
  result: {
    results: SalesNavProfile[];
    paging: SalesNavPaging;
  };
};

export type SalesNavSearchCompaniesFilter = {
  annualRevenue?: {
    min: number | string;
    max: number | string;
    currency?: LinkedinFilterValue;
  } | null;
  headcount?: LinkedinFilterValue[] | null;
  headcountGrowth?: { min?: number; max?: number } | null;
  industry?: (LinkedinFilterValue & LinkedinExcludeFilter)[] | string[] | null;
  headquarterLocation?: (LinkedinFilterValue & LinkedinExcludeFilter)[] | null;
  headquarterPostalCode?:
    | (LinkedinFilterValue & LinkedinExcludeFilter)[]
    | null;
  hiringOnLinkedin?: boolean | null;
  departmentHeadcount?: {
    min?: number; // percentage
    max?: number;
    department: LinkedinFilterValue;
  } | null;
  departmentHeadcountGrowth?: {
    min?: number; // percentage
    max?: number;
    department: LinkedinFilterValue;
  } | null;
};

export type SalesNavSearchCompaniesFilterFromSaved =
  SalesNavSearchCompaniesFilter & {
    annualRevenue?: {
      min?: number | string;
      max?: number | string;
      subFilter?: LinkedinFilterValue;
    } | null;
    departmentHeadcount?: {
      min?: string; // percentage
      max?: string;
      subFilter: LinkedinFilterValue;
    } | null;
    departmentHeadcountGrowth?: {
      min?: string; // percentage
      max?: string;
      subFilter: LinkedinFilterValue;
    } | null;
  };

export type CronoGatewayLinkedinSearchSalesNavCompaniesAction = {
  identifiers: null;
  target: 'linkedin';
  methodName: 'searchSalesNavCompanies';
  params: SalesNavSearchCompaniesFilter & {
    keywords?: string | null;

    // pagination
    start?: number | null;
    count?: number | null;
  };
  result: {
    results: SalesNavCompany[];
    paging: SalesNavPaging;
  };
};

export type CronoGatewayLinkedinHasSalesNavAction = {
  identifiers: null;
  target: 'linkedin';
  methodName: 'hasSalesNav';
  params: void;
  result: boolean;
};

export type CronoGatewayLinkedinHasSalesNav2Action = {
  identifiers: null;
  target: 'linkedin';
  methodName: 'hasSalesNav2';
  params: void;
  result: {
    hasSalesNav: boolean;
    hasPremium: boolean;
  };
};

export type CronoGatewayLinkedinSearchCompaniesAction = {
  identifiers: null;
  target: 'linkedin';
  methodName: 'searchCompanies';
  params: {
    keywords?: string;
    filters?: {
      industry?:
        | string[]
        | (LinkedinFilterValue & LinkedinExcludeFilter)[]
        | null;
      location?: string[] | null;
      companySize?: string[] | null;
    } | null;
    page?: number;
  };
  result: {
    paging: LinkedinPaging;
    companies: LinkedinCompany[];
  };
};

export type LinkedinPaging = {
  total: number;
  start: number;
  count: number;
  totalResults: number;
};

export type SalesNavPaging = {
  total: number;
  start: number;
  count: number;
};

export type CronoGatewayLinkedinGetCompanyInfoAction = {
  identifiers: 'account';
  target: 'linkedin';
  methodName: 'getCompanyInfo';
  params: {
    publicId?: string;
    company?: LinkedinCompany;
  };
  result: {
    company: LinkedinCompanyCompleteInfo | null;
  };
};

export type CronoGatewayLinkedinGetInvitationsLimitAction = {
  identifiers: null;
  target: 'linkedin';
  methodName: 'getInvitationsLimit';
  params: void;
  result: {
    invitationsRemaining: number | null;
    maxMessageLength: number | null;
    hasSalesNav: null;
    hasPremium: boolean | null;
  };
};

export type CronoGatewayLinkedinGetAllConversationsAction = {
  identifiers: null;
  target: 'linkedin';
  methodName: 'getAllConversations';
  params: void;
  result: { conversations: LinkedinConversation[] };
};

export type CronoGatewayLinkedinGetSuggestedCompaniesAction = {
  identifiers: 'account';
  target: 'linkedin';
  methodName: 'getSuggestedCompanies';
  params: {
    publicId?: string;
    company?: LinkedinCompany;
  };
  result: {
    companies: LinkedinCompanyCompleteInfo[] | null;
  };
};

export type CronoGatewayLinkedinGetProfileInfoAction = {
  identifiers: 'prospect';
  target: 'linkedin';
  methodName: 'getProfileInfo';
  params: { publicId?: string };
  result: { profile: LinkedinProfileCompleteInfo | null };
};

export type CronoGatewayLinkedinGetInvitationsAction = {
  identifiers: null;
  target: 'linkedin';
  methodName: 'getAllInvitations';
  params: void;
  result: {
    invitations?: { profile: LinkedinMiniProfile }[];
  };
};

export type CronoGatewayLinkedinGetProfileIdFromLeadIdAction = {
  identifiers: null;
  target: 'linkedin';
  methodName: 'getPublicIdFromLeadId';
  params: { leadId: string };
  result: {
    publicId?: string;
  };
};

export type CronoGatewayLinkedinGetCompanyInfoByNumericIdAction = {
  identifiers: null;
  target: 'linkedin';
  methodName: 'getCompanyInfoByNumericId';
  params: { numericId: string };
  result: Company | null;
};

export type CronoGatewayLinkedinGetCompanyNumericIdAction = {
  identifiers: 'account';
  target: 'linkedin';
  methodName: 'getCompanyNumericId';
  params: { publicId?: string };
  result: { numericId: string | null };
};

export type CronoGatewayLinkedinGetUserExperienceAction = {
  identifiers: 'prospect';
  target: 'linkedin';
  methodName: 'getUserExperience';
  params: { publicId?: string; profileUrn?: string };
  result: Company[];
};

export type CronoGatewayLinkedinGetThreadReactionsAction = {
  identifiers: null;
  target: 'linkedin';
  methodName: 'getThreadReactions';
  params: {
    count?: number;
    start?: number;
    threadUrn: string | string[];
  };
  result: {
    leads: ListLead[];
    paging: LinkedinPaging;
  };
};

export type CronoGatewayLinkedinGetJobChangesAction = {
  identifiers: null;
  target: 'linkedin';
  methodName: 'getJobChanges';
  params: {
    start?: number;
    count?: number;
  };
  result: {
    jobChanges: LinkedinJobChangeInfo[];
    paging: LinkedinPaging;
  };
};

export type CronoGatewayLinkedinSendMessageWithAttachmentAction = {
  identifiers: 'prospect';
  target: 'linkedin';
  methodName: 'sendMessageWithAttachment';
  params: {
    publicId?: string;
    fileName: string | string[];
    attachmentUrlData: string | string[];
    text?: string | null;
  };
  result: {
    messageSent: boolean;
    connectionStatus: LinkedinConnectionStatus;
    // Sent text plus the attached media (link or label) to log in Crono.
    // Missing on older extension versions
    loggedContent?: string;
  };
};

export type CronoGatewayLinkedinSendVoiceMessageAction = {
  identifiers: 'prospect';
  target: 'linkedin';
  methodName: 'sendVoiceMessage';
  params: {
    publicId?: string;
    voiceMessageUrlData: string;
    duration: number;
  };
  result: {
    messageSent: boolean;
    connectionStatus: LinkedinConnectionStatus;
  };
};

export type CronoGatewayLinkedinGetInMailCreditsAction = {
  identifiers: null;
  target: 'linkedin';
  methodName: 'getInMailCredits';
  params: void;
  result: number;
};

export type CronoGatewayLinkedinGetSalesNavConversationAction = {
  identifiers: 'prospect';
  target: 'linkedin';
  methodName: 'getSalesNavConversation';
  params: {
    thread?: string;
    profileId?: string;
    leadId?: string;
  };
  result: LinkedinSalesNavMessage[];
};

export type CronoGatewayLinkedinGetSalesNavConversationIdAction = {
  identifiers: 'prospect';
  target: 'linkedin';
  methodName: 'getSalesNavConversationId';
  params: {
    profileId?: string;
  };
  result: string;
};

export type CronoGatewayLinkedinGetSalesNavAllConversationsAction = {
  identifiers: null;
  target: 'linkedin';
  methodName: 'getSalesNavAllConversations';
  params: void;
  result: { conversations: LinkedinConversation[] };
};

export type CronoGatewayLinkedinGetRecentConnections = {
  identifiers: null;
  target: 'linkedin';
  methodName: 'getRecentConnections';
  params: {
    count?: number;
    start?: number;
  };
  result: LinkedinRecentConnection[];
};

export type CronoGatewayLinkedinSendInMailMessageAction = {
  identifiers: 'prospect';
  target: 'linkedin';
  methodName: 'sendInMailMessage';
  params: {
    publicId?: string;
    subject: string;
    body: string;
    attachments?: {
      fileName: string;
      urlData: string;
    }[];
  };
  result: {
    messageSent: boolean;
    rawError?: string | null;
    error?: string | null;
  };
};

export type CronoGatewayLinkedinDownloadFileAction = {
  identifiers: null;
  target: 'linkedin';
  methodName: 'downloadFile';
  params: { url: string };
  result: {
    urlData: string;
  };
};

export type CronoGatewayLinkedinGetFilterValuesAction =
  | {
      identifiers: null;
      target: 'linkedin';
      methodName: 'getLinkedinFilterValues';
      params:
        | {
            type: 'salesNav';
            filterName:
              | 'FUNCTION'
              | 'TITLE'
              | 'BING_GEO'
              | 'INDUSTRY'
              | 'PERSONA'
              | 'BING_GEO_POSTAL_CODE'
              | 'PROFILE_LANGUAGE';
            keyword: string | null;
          }
        | {
            type: 'linkedin';
            filterName: 'GEO' | 'INDUSTRY';
            keyword: string | null;
          };
      result: { values: LinkedinFilterValue[] };
    }
  | {
      identifiers: null;
      target: 'linkedin';
      methodName: 'getLinkedinFilterValues';
      params:
        | {
            type: 'salesNav';
            filterName: 'COMPANY_WITH_LIST';
            keyword: string | null;
          }
        | {
            type: 'linkedin';
            filterName: 'COMPANY';
            keyword: string | null;
          };
      result: { values: LinkedinFilterValueWithImg[] };
    };

export type LinkedinFilterValue = {
  id: string;
  label: string;
  exclude?: boolean;
};

export type LinkedinExcludeFilter = {
  exclude?: boolean;
};

export type LinkedinFilterValueWithImg = LinkedinFilterValue & {
  imageUrl?: string | null;
};

export type LinkedinFilterValueFreeSolo = { id?: string | null; label: string };

export type LinkedinFilterValueFreeSoloWithImage = {
  id?: string | null;
  label: string;
  imageUrl?: string | null;
};

export type CronoGatewayLinkedinRefreshCurrentSalesNavCookie = {
  identifiers: null;
  target: 'linkedin';
  methodName: 'refreshCurrentSalesNavCookie';
  params: Record<string, never>;
  result: void;
};

// Linkedin models

export type LinkedinConnectionStatus =
  | 'Unknown'
  | 'Connected'
  | 'NotConnected'
  | 'InvitationSent';

export type LinkedinSalesNavMessage = LinkedinMessage & {
  subject?: string;
  read?: boolean;
};

export type LinkedinMessage = {
  profileId: string;
  message: string;
  user: string;
  timestamp: number;
  sent: boolean;
  voiceMessage?: {
    durationMillis: number;
    url: string;
  };
  attachments?: {
    url: string;
    name: string;
    mediaType: string;
    byteSize: number;
  }[];
  senderPictureUrl?: string;
  senderPictureUrlData?: string;
};

export type LinkedinActivityType = 'post' | 'comment' | 'event';

export type LinkedinActivity = {
  content: string;
  type: LinkedinActivityType;
  date: string;
  calculatedDate?: Date | null;
  dateExtended: string;
  author?: {
    name: string;
    publicId: string;
  };
  url?: string;
  urn?: string;
  ugcPost?: string;
  isRepost?: boolean;
  hashtags: string[];
};

export type LinkedinReaction = 'LIKE';

export type LinkedinJobPost = {
  title: string;
  date?: Date;
  location: string;
  jobId: string;
  jobUrl: string;
};

export interface LinkedinProfile {
  profileId: string;
  trackingId?: string;
  url: string;
  firstName: string;
  lastName: string;
  title: string;
  pictureUrl: string | null;
  publicId: string;
  location?: string;
  countryCode?: string | null;
  timeZone?: string | null;
  distance?: string | null;
}

export interface SalesNavProfile {
  lastName: string;
  objectUrn: string;
  geoRegion: string;
  title: string;
  premium: boolean;
  currentPositions?: {
    tenureAtPosition?: {
      numYears: number;
      numMonths: number;
    };
    companyName: string;
    title: string;
    companyUrnResolutionResult: {
      name: string;
      industry: string;
      location: string;
    };
    companyUrn: string;
    companyNumericId: string;
    current: boolean;
    tenureAtCompany?: {
      numYears: number;
      numMonths: number;
    };
    startedOn?: {
      month: number;
      year: number;
    };
  }[];
  entityUrn: string;
  leadId: string;
  viewed: boolean;
  profilePictureDisplayImage: {
    artifacts: {
      width: number;
      fileIdentifyingUrlPathSegment: string;
      height: number;
    }[];
    rootUrl: string;
  };
  pictureUrl?: string;
  pendingInvitation: boolean;
  degree: number;
  fullName: string;
  firstName: string;
}

export interface SalesNavCompany {
  saved?: boolean;
  companyName?: string;
  description?: string;
  companyPictureDisplayImage?: {
    rootUrl: string;
    artifacts: {
      width: number;
      fileIdentifyingUrlPathSegment: string;
      height: number;
    }[];
  };
  pictureUrl?: string;
  industry?: string;
  listCount?: number;
  employeeCountRange?: string;
  revenueHighlights?: {
    estMaxRev?: number;
    id?: string;
    estMinRev?: number;
  };
  employeeDisplayCount?: string;
  entityUrn?: string;
  numericId?: string;
  hiringOnLinkedin?: true | null;
}

export interface LinkedinCompany {
  title: string;
  primarySubtitle?: string;
  url: string;
  followerCount: number;
  logoUrl: string;
  publicId?: string;
  numericId?: string;

  // hack to quickly make ts happy
  website?: undefined;
}

export function IsLinkedinCompany(object: unknown): object is LinkedinCompany {
  if (typeof object !== 'object' || object == null) return false;
  return (
    'logoUrl' in object || 'primarySubtitle' in object || 'title' in object
  );
}

export type Company = {
  name: string | null;
  publicId?: string | null;
  numericId?: string | null;
  active: boolean;
  role?: string | null;
  previousRoles?: string[];
  numberOfEmployees?: number | null;
  industry?: string | null;
  website?: string | null;
  tagline?: string | null;
  description?: string | null;
  url?: string | null;
  onlyInCrm?: boolean | null;
  objectId?: string | null;
  imageUrl?: string | null;
};

export type LinkedinConversation = {
  type:
    | 'linkedinMessage'
    | 'salesNavMessage'
    | 'inMail'
    | 'inMailAccept'
    | 'linkedinVoicenote';
  conversationId?: string;
  sender?: string;
  senderProfileId?: string;
  participant?: string;
  participantProfileId?: string;
  participantPublicId?: string;
  lastActivity?: number;
  lastMessage?: string;
  subject?: string;
  isSent?: boolean;
  read?: boolean;
};

export interface LinkedinCompanyCompleteInfo {
  name: string | null;
  publicId: string | null;
  numberOfEmployees: number | null;
  employeeRangeStart: number | null;
  employeeRangeEnd: number | null;
  industryId: string | null;
  industry: string | null;
  website: string | null;
  tagline: string | null;
  description: string | null;
  url: string | null;
  numericId: string | null;
  phone: string | null;
  specialities: string[] | null;
  geographicArea: string | null;
  city: string | null;
  country: string | null;
  countryCode: string | null;
  location: string | null;
  //The duplication is just for easiness in getting them when sending to the scrape database, since there we expect pictureUrl
  imageUrl?: string | null;
  pictureUrl?: string | null;
  unclaimed?: boolean;
}

export interface LinkedinCompanyShortInfo {
  name: string | null;
  url: string | null;
  numericId: string | null;
}

export interface LinkedinProfileCompleteInfo {
  currentCompany: LinkedinCompanyShortInfo | null;
  firstName: string | null;
  lastName: string | null;
  headline: string | null;
  publicId: string | null;
  profileId: string | null;
  leadId: string | null;
  country: string | null;
  countryCode: string | null;
  timeZone?: string | null;
  url: string | null;
}

export interface LinkedinWorkExperience {
  position: string;
  description: string | null;
  timePeriod?: {
    months: number;
    years: number;
    startDate: {
      month: number;
      year: number;
    };
    endDate?: {
      month: number;
      year: number;
    };
  };
  /**
   * Start of `position` itself. LinkedIn groups several roles held at the same
   * company into one experience, and there `timePeriod.startDate` is when the
   * prospect joined the company, not when the current role began. Only set when
   * the producer actually knows the role's own start date — a single experience
   * entry whose title was edited in place carries no such date.
   */
  positionStartDate?: {
    month: number;
    year: number;
  };
  company: {
    name: string;
    numericId: string | null;
    publicId: string | null;
  };
}

export interface LinkedinProfileExtendedInfo {
  firstName: string | null;
  lastName: string | null;
  pictureUrl: string | null;
  seniorityLevel: string;
  overallExperienceTime: object | null;
  location: string;
  description: string | null;
  connectionLevel: string | null;
  connectionStatus: LinkedinConnectionStatus;
  workExperience: LinkedinWorkExperience[];
  totalExperiences: number | null;
  // Numeric id of the active/current company. Set only by the FindNew mapper,
  // where `workExperience` is not ordered current-first, so consumers can pick
  // the right active experience instead of blindly taking the first one. The
  // LinkedIn getProfileView path leaves this undefined (already current-first).
  currentCompanyNumericId?: string | null;
  title?: string | null;
  objectUrn: string | null;
  profileUrn: string | null;
  publicId: string | null;
  profileId: string | null;
  leadId: string | null;
  countryCode: string | null;
  timeZone?: string | null;
}

export interface LinkedinCompanyExtendedInfo {
  numericId?: string;
  name?: string;
  url?: string;
  imageUrl?: string;
  industry?: string;
  category?: string;
  city?: string;
  country?: string;
  description?: string;
  growth?: string;
  activities?: CronoGatewayLinkedinGetActivitiesAction['result'];
  specialities?: string[];
  foundedOn?: string;
  hashtags?: string[];
  fundingData?: FundingData | null;
  numberOfEmployees?: number | null;
  employeeCountRange?: string;
  salesNavCompanyUrl?: string;
}

export interface FundingData {
  numberOfFundingRounds?: number;
  fundingRound?: string;
  amount?: string;
  announcedDate?: string;
  crunchbaseUrl?: string;
}
export interface LinkedinInvitation {
  profile: LinkedinMiniProfile[];
}

export interface LinkedinMiniProfile {
  firstName: string;
  lastName: string;
  profileId: string;
  publicIdentifier: string;
}

export type ListLead = {
  source: 'linkedin' | 'salesnav';
  name: string;
  firstName: string;
  lastName: string;
  linkedin: string;
  title: string;
  location?: string | null;
  linkedinStatus: LinkedinConnectionStatus;
  image?: string;
  currentCompany: {
    name: string;
    linkedin: string;
    publicId: string | null;
    numericId: string | null;
  } | null;
  toAdd?: boolean;
} & (
  | {
      // linkedin
      publicId: string;
      profileId: string | null;
      leadId: null;
      g2PublicId: null;
    }
  | {
      // salesnav
      publicId: null;
      profileId: null;
      leadId: string;
      g2PublicId: null;
    }
);

export type ListCompany = {
  name: string;
  linkedin: string;
  numberOfEmployees?: number | null;
  industry?: string | null;
  website?: string | null;
  toAdd?: boolean;
} & (
  | {
      // linkedin
      publicId: string;
      numericId: null;
    }
  | {
      // salesnav
      publicId: null;
      numericId: string;
    }
);

export type LinkedinJobType = 'newCompany' | 'newRole' | null;

export interface LinkedinJobChangeInfo {
  company: string;
  position: string;
  name: string;
  publicId: string;
  datetime: Date | null;
  type: LinkedinJobType;
}

export type LinkedinRecentConnection = {
  firstName: string;
  lastName: string;
  publicId: string;
  profileId: string;
  connectionTimestamp: number;
};

export interface Artifact {
  width: number;
  fileIdentifyingUrlPathSegment: string;
  height: number;
}

export interface ComLinkedinCommonVectorImage {
  artifacts: Artifact[];
  rootUrl: string;
}

export interface ProfileBackgroundPicture {
  'com.linkedin.common.VectorImage': ComLinkedinCommonVectorImage;
}

export type ProfileView = {
  positionGroupView: {
    paging: {
      start: number;
      count: number;
      total: number;
      links: string[];
    };
    entityUrn: `urn:li:fs_positionGroupView:${string}`;
    profileId: string;
    elements: [
      {
        timePeriod: {
          startDate: {
            month: number;
            year: number;
          };
        };
        name: string;
        positions: {
          locationName: string;
          entityUrn: `urn:li:fs_position:(${string})`;
          $anti_abuse_metadata: {
            '/geoUrn': {
              sourceUrns: {
                'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
              };
            };
            '/companyName': {
              sourceUrns: {
                'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
              };
            };
            '/companyUrn': {
              sourceUrns: {
                'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
              };
            };
            '/entityUrn': {
              sourceUrns: {
                'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
              };
            };
            '/geoLocationName': {
              sourceUrns: {
                'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
              };
            };
            '/promotion': {
              sourceUrns: {
                'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
              };
            };
            '/title': {
              sourceUrns: {
                'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
              };
            };
            '/region': {
              sourceUrns: {
                'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
              };
            };
            '/locationName': {
              sourceUrns: {
                'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
              };
            };
            '/description': {
              sourceUrns: {
                'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
              };
            };
          };
          geoLocationName: string;
          geoUrn: `urn:li:fs_geo:${string}`;
          companyName: string;
          timePeriod: {
            startDate: {
              month: number;
              year: number;
            };
            endDate?: {
              month: number;
              year: number;
            };
          };
          description?: string;
          company: {
            miniCompany: {
              objectUrn: `urn:li:company:${string}`;
              entityUrn: `urn:li:fs_miniCompany:${string}`;
              name: string;
              showcase: boolean;
              active: boolean;
              universalName: string;
              dashCompanyUrn: `urn:li:fsd_company:${string}`;
              trackingId: string;
            };
            employeeCountRange: {
              start: number;
            };
            industries: string[];
          };
          title: string;
          region: string;
          companyUrn: `urn:li:fs_miniCompany:${string}`;
        }[];
        paging: {
          start: number;
          count: number;
          total: number;
          links: string[];
        };
        miniCompany: {
          objectUrn: 'urn:li:company:1035';
          entityUrn: 'urn:li:fs_miniCompany:1035';
          name: 'Microsoft';
          showcase: false;
          active: true;
          universalName: 'microsoft';
          dashCompanyUrn: 'urn:li:fsd_company:1035';
          trackingId: 'CSUujdocRzu0w51V18m4OQ==';
        };
        entityUrn: 'urn:li:fs_positionGroup:(ACoAABnaQzIByY9SabMbXP3UOoVVarAERV_Cf_M,0)';
      },
    ];
  };
  patentView: {
    paging: {
      start: number;
      count: number;
      total: number;
      links: string[];
    };
    entityUrn: `urn:li:fs_patentView:${string}`;
    profileId: string;
    elements: unknown;
  };
  summaryTreasuryMediaCount: number;
  summaryTreasuryMedias: unknown[];
  educationView: {
    paging: {
      start: number;
      count: number;
      total: number;
      links: string[];
    };
    entityUrn: `urn:li:fs_educationView:${string}`;
    profileId: string;
    elements: {
      entityUrn: `urn:li:fs_education:(${string},${string})`;
      school: {
        objectUrn: `urn:li:school:${string}`;
        entityUrn: `urn:li:fs_miniSchool:${string}`;
        active: boolean;
        schoolName: string;
        trackingId: string;
      };
      $anti_abuse_metadata: {
        '/schoolUrn': {
          sourceUrns: {
            'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
          };
        };
        '/degreeUrn': {
          sourceUrns: {
            'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
          };
        };
        '/fieldOfStudy': {
          sourceUrns: {
            'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
          };
        };
        '/activities': {
          sourceUrns: {
            'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
          };
        };
        '/entityUrn': {
          sourceUrns: {
            'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
          };
        };
        '/grade': {
          sourceUrns: {
            'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
          };
        };
        '/fieldOfStudyUrn': {
          sourceUrns: {
            'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
          };
        };
        '/schoolName': {
          sourceUrns: {
            'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
          };
        };
        '/degreeName': {
          sourceUrns: {
            'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
          };
        };
        '/description': {
          sourceUrns: {
            'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
          };
        };
      };
      grade: string;
      timePeriod: {
        endDate?: {
          year: number;
        };
        startDate: {
          year: number;
        };
      };
      degreeName: string;
      schoolName: string;
      fieldOfStudy: string;
      honors?: `urn:li:fs_honor:(${string},${string})`[];
      schoolUrn: `urn:li:fs_miniSchool:${string}`;
    }[];
  };
  organizationView: {
    paging: {
      start: number;
      count: number;
      total: number;
      links: string[];
    };
    entityUrn: `urn:li:fs_organizationView:${string}`;
    profileId: string;
    elements: unknown[];
  };
  projectView: {
    paging: {
      start: number;
      count: number;
      total: number;
      links: string[];
    };
    entityUrn: `urn:li:fs_projectView:${string}`;
    profileId: string;
    elements: unknown[];
  };
  positionView: {
    paging: {
      start: number;
      count: number;
      total: number;
      links: string[];
    };
    entityUrn: `urn:li:fs_positionView:${string}`;
    profileId: string;
    elements: {
      locationName: string;
      entityUrn: `urn:li:fs_position:(${string},${string})`;
      $anti_abuse_metadata: {
        '/geoUrn': {
          sourceUrns: {
            'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
          };
        };
        '/companyName': {
          sourceUrns: {
            'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
          };
        };
        '/companyUrn': {
          sourceUrns: {
            'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
          };
        };
        '/entityUrn': {
          sourceUrns: {
            'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
          };
        };
        '/geoLocationName': {
          sourceUrns: {
            'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
          };
        };
        '/promotion': {
          sourceUrns: {
            'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
          };
        };
        '/title': {
          sourceUrns: {
            'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
          };
        };
        '/region': {
          sourceUrns: {
            'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
          };
        };
        '/locationName': {
          sourceUrns: {
            'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
          };
        };
        '/description': {
          sourceUrns: {
            'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
          };
        };
      };
      geoLocationName: string | null;
      description: string | null;
      geoUrn: `urn:li:fs_geo:${string}`;
      companyName: string;
      timePeriod?: {
        startDate: {
          month?: number;
          year: number;
        };
        endDate?: {
          month?: number;
          year: number;
        };
      };
      company: {
        miniCompany: {
          objectUrn: `urn:li:company:${string}`;
          entityUrn: `urn:li:fs_miniCompany:${string}`;
          name: string;
          showcase: boolean;
          active: boolean;
          universalName: string;
          dashCompanyUrn: `urn:li:fsd_company:${string}`;
          trackingId: string;
        };
        employeeCountRange: {
          start: number;
        };
        industries: string[];
      };
      title: string;
      region: string;
      companyUrn?: `urn:li:fs_miniCompany:${string}`;
    }[];
  };
  profile: {
    summary: string;
    industryName: string;
    lastName: string;
    supportedLocales: [
      {
        country: string;
        language: string;
      },
    ];
    locationName: string;
    geoLocationName: string | null;
    student: boolean;
    $anti_abuse_metadata: {
      '/lastName': {
        sourceUrns: {
          'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
        };
      };
      '/phoneticFirstName': {
        sourceUrns: {
          'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
        };
      };
      '/geoCountryUrn': {
        sourceUrns: {
          'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
        };
      };
      '/entityUrn': {
        sourceUrns: {
          'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
        };
      };
      '/elt': {
        sourceUrns: {
          'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
        };
      };
      '/firstName': {
        sourceUrns: {
          'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
        };
      };
      '/summary': {
        sourceUrns: {
          'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
        };
      };
      '/address': {
        sourceUrns: {
          'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
        };
      };
      '/geoLocationName': {
        sourceUrns: {
          'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
        };
      };
      '/industryName': {
        sourceUrns: {
          'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
        };
      };
      '/geoLocationBackfilled': {
        sourceUrns: {
          'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
        };
      };
      '/phoneticLastName': {
        sourceUrns: {
          'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
        };
      };
      '/versionTag': {
        sourceUrns: {
          'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
        };
      };
      '/maidenName': {
        sourceUrns: {
          'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
        };
      };
      '/student': {
        sourceUrns: {
          'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
        };
      };
      '/headline': {
        sourceUrns: {
          'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
        };
      };
      '/locationName': {
        sourceUrns: {
          'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
        };
      };
      '/geoCountryName': {
        sourceUrns: {
          'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
        };
      };
      '/industryUrn': {
        sourceUrns: {
          'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
        };
      };
      '/showEducationOnProfileTopCard': {
        sourceUrns: {
          'com.linkedin.common.urn.MemberUrn': `urn:li:member:${string}`;
        };
      };
    };
    geoCountryName: string;
    geoCountryUrn: `urn:li:fs_geo:${string}`;
    versionTag: string;
    geoLocationBackfilled: boolean;
    elt: boolean;
    industryUrn: string;
    defaultLocale: {
      country: string;
      language: string;
    };
    firstName: string;
    showEducationOnProfileTopCard: boolean;
    entityUrn: `urn:li:fs_profile:${string}`;
    geoLocation: {
      geoUrn: `urn:li:fs_geo:${string}`[];
    };
    location: {
      basicLocation: {
        countryCode: string;
      };
    };
    miniProfile: {
      firstName: string;
      lastName: string;
      dashEntityUrn: `urn:li:fsd_profile:${string}`;
      occupation: 'Senior Software Engineer at Microsoft';
      objectUrn: `urn:li:member:${string}`;
      entityUrn: `urn:li:fs_miniProfile:${string}`;
      publicIdentifier: string;
      trackingId: string;
      picture?: ProfileBackgroundPicture;
    };
    headline: string;
  };
  languageView: {
    paging: {
      start: number;
      count: number;
      total: number;
      links: string[];
    };
    entityUrn: `urn:li:fs_languageView:${string}`;
    profileId: string;
    elements: {
      name: 'English';
      entityUrn: `urn:li:fs_language:(${string},${string})`;
      proficiency: 'NATIVE_OR_BILINGUAL' | string;
    }[];
  };
  certificationView: {
    paging: {
      start: number;
      count: number;
      total: number;
      links: string[];
    };
    entityUrn: `urn:li:fs_certificationView:${string}`;
    profileId: string;
    elements: {
      entityUrn: `urn:li:fs_certification:(${string},${string})`;
      authority: string;
      name: string;
      timePeriod: {
        startDate: {
          month: number;
          year: number;
        };
      };
      licenseNumber: string;
      company: {
        objectUrn: `urn:li:company:${string}`;
        entityUrn: `urn:li:fs_miniCompany:${string}`;
        name: string;
        showcase: boolean;
        active: boolean;
        universalName: string;
        dashCompanyUrn: `urn:li:fsd_company:${string}`;
        trackingId: string;
      };
      displaySource: string;
      companyUrn: `urn:li:fs_miniCompany:${string}`;
      url: string;
    }[];
  };
  testScoreView: {
    paging: {
      start: number;
      count: number;
      total: number;
      links: string[];
    };
    entityUrn: `urn:li:fs_testScoreView:${string}`;
    profileId: string;
    elements: unknown[];
  };
  volunteerCauseView: {
    paging: {
      start: number;
      count: number;
      total: number;
      links: string[];
    };
    entityUrn: `urn:li:fs_volunteerCauseView:${string}`;
    profileId: string;
    elements: unknown[];
  };
  entityUrn: `urn:li:fs_profileView:${string}`;
  courseView: {
    paging: {
      start: number;
      count: number;
      total: number;
      links: string[];
    };
    entityUrn: `urn:li:fs_courseView:${string}`;
    profileId: string;
    elements: unknown[];
  };
  honorView: {
    paging: {
      start: number;
      count: number;
      total: number;
      links: string[];
    };
    entityUrn: `urn:li:fs_honorView:${string}`;
    profileId: string;
    elements: {
      occupation: `urn:li:fs_education:(${string},${string})`;
      title: string;
      issueDate: {
        month: number;
        year: number;
      };
      entityUrn: `urn:li:fs_honor:(${string},${string})`;
      issuer: string;
    }[];
  };
  skillView: {
    paging: {
      start: number;
      count: number;
      total: number;
      links: string[];
    };
    entityUrn: 'urn:li:fs_skillView:ACoAABnaQzIByY9SabMbXP3UOoVVarAERV_Cf_M';
    profileId: 'ACoAABnaQzIByY9SabMbXP3UOoVVarAERV_Cf_M';
    elements: [
      {
        name: 'OpenCV';
        entityUrn: 'urn:li:fs_skill:(ACoAABnaQzIByY9SabMbXP3UOoVVarAERV_Cf_M,42)';
      },
      {
        name: 'Verilog';
        entityUrn: 'urn:li:fs_skill:(ACoAABnaQzIByY9SabMbXP3UOoVVarAERV_Cf_M,41)';
      },
      {
        name: 'Non-Volatile Memory';
        entityUrn: 'urn:li:fs_skill:(ACoAABnaQzIByY9SabMbXP3UOoVVarAERV_Cf_M,40)';
      },
      {
        name: 'Computer Vision';
        entityUrn: 'urn:li:fs_skill:(ACoAABnaQzIByY9SabMbXP3UOoVVarAERV_Cf_M,38)';
      },
    ];
  };
  volunteerExperienceView: {
    paging: {
      start: number;
      count: number;
      total: number;
      links: string[];
    };
    entityUrn: 'urn:li:fs_volunteerExperienceView:ACoAABnaQzIByY9SabMbXP3UOoVVarAERV_Cf_M';
    profileId: 'ACoAABnaQzIByY9SabMbXP3UOoVVarAERV_Cf_M';
    elements: [
      {
        role: 'Student Representative, Quality Committee Member, and Student Mentor';
        entityUrn: 'urn:li:fs_volunteerExperience:(ACoAABnaQzIByY9SabMbXP3UOoVVarAERV_Cf_M,240921347)';
        companyName: 'Universidad de Zaragoza';
        timePeriod: {
          endDate: {
            month: 6;
            year: 2017;
          };
          startDate: {
            month: 9;
            year: 2013;
          };
        };
        company: {
          miniCompany: {
            objectUrn: 'urn:li:company:166616';
            entityUrn: 'urn:li:fs_miniCompany:166616';
            name: 'Universidad de Zaragoza';
            showcase: false;
            active: true;
            universalName: 'universidad-de-zaragoza';
            dashCompanyUrn: 'urn:li:fsd_company:166616';
            trackingId: 'EAjyOoEkSoCySGPxzqXHLg==';
          };
        };
        companyUrn: 'urn:li:fs_miniCompany:166616';
      },
    ];
  };
  primaryLocale: {
    country: string;
    language: string;
  };
  publicationView: {
    paging: {
      start: number;
      count: number;
      total: number;
      links: string[];
    };
    entityUrn: `urn:li:fs_publicationView:${string}`;
    profileId: string;
    elements: unknown[];
  };
};

export type CronoGatewaySalesNavGetSavedSearchesLeads = {
  identifiers: null;
  target: 'linkedin';
  methodName: 'getSalesNavSavedSearchesLeads';
  params: void;
  result: {
    savedSearches: SalesNavSavedSearch[];
  };
};

export type SalesNavSavedSearch = {
  name: string;
  id: number;
  newHitsCount: number;
  lastViewedAt?: number;
  createdAt: number;
  keywords: string | null;
};

export type CronoGatewaySalesNavGetSavedSearchesCompanies = {
  identifiers: null;
  target: 'linkedin';
  methodName: 'getSalesNavSavedSearchesCompanies';
  params: void;
  result: {
    savedSearches: SalesNavSavedSearch[];
  };
};

export type CronoGatewaySalesNavExecuteSavedSearchLeads = {
  identifiers: null;
  target: 'linkedin';
  methodName: 'executeSalesNavSavedSearchesLeads';
  params: {
    savedSearchId: number;
  };
  result: {
    filters: SalesNavSearchLeadsFilter;
    keywords: string | null;
    results: SalesNavProfile[];
    paging: SalesNavPaging;
  };
};

export type CronoGatewaySalesNavExecuteSavedSearchCompanies = {
  identifiers: null;
  target: 'linkedin';
  methodName: 'executeSalesNavSavedSearchesCompanies';
  params: {
    savedSearchId: number;
  };
  result: {
    filters: SalesNavSearchCompaniesFilterFromSaved;
    keywords: string | null;
    results: SalesNavCompany[];
    paging: SalesNavPaging;
  };
};
